import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSortableResults } from '../search/useSortableResults';
import ResultDetailDialog from './ResultDetailDialog';
import type { ProjectResult } from '../../types/api';

type SortKey = 'index' | 'similarity' | 'name' | 'year' | 'type';

interface ResultsTableProps {
  results: ProjectResult[];
}

export default function ResultsTable({ results }: ResultsTableProps) {
  const { t } = useTranslation('projects');
  const [dialogResult, setDialogResult] = useState<ProjectResult | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { sortedResults, HeaderButton } = useSortableResults<ProjectResult, SortKey>(
    results,
    { numericKeys: ['similarity', 'year'] },
  );

  const openDialogFor = (result: ProjectResult, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    setDialogResult(result);
    setDialogOpen(true);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-center">
              <HeaderButton column="index" label={t('table.no')} align="center" />
            </TableHead>
            <TableHead className="w-28 text-center">
              <HeaderButton column="similarity" label={t('table.similarity')} align="center" />
            </TableHead>
            <TableHead>
              <HeaderButton column="name" label={t('table.name')} />
            </TableHead>
            <TableHead className="w-20 text-center">
              <HeaderButton column="year" label={t('table.year')} align="center" />
            </TableHead>
            <TableHead className="w-40">
              <HeaderButton column="type" label={t('table.type')} />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedResults.map((result, index) => (
            <TableRow key={result.id}>
              <TableCell className="text-center text-muted-foreground tabular-nums">
                {index + 1}
              </TableCell>
              <TableCell className="text-center font-medium tabular-nums">
                {typeof result.similarity === 'number'
                  ? result.similarity.toFixed(2)
                  : result.similarity ?? '—'}
              </TableCell>
              <TableCell>
                <a
                  href={`/outcome/${result.id}`}
                  onClick={(e) => openDialogFor(result, e)}
                  className="font-medium text-primary hover:underline"
                >
                  {result.name}
                </a>
              </TableCell>
              <TableCell className="text-center tabular-nums text-muted-foreground">
                {result.year || '—'}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {result.type || '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ResultDetailDialog
        result={dialogResult}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
